import {
  Badge,
  Box,
  Container,
  Flex,
  Text,
  TextInput,
  ActionIcon,
  Collapse,
  Table,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconSearch, IconChevronDown, IconChevronUp } from "@tabler/icons";
import { MantineReactTable } from "mantine-react-table";
import React, { useMemo, useState } from "react";
import { useQuery } from "react-query";
import AppBreadcrumb from "components/AppBreadcrumb";
import moment from "moment";
import { httpClient } from "lib/axios";

export default function WarehouseMergeReport() {
  let isMobileScreen = useMediaQuery("(max-width: 768px)");

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // ─── Fetch Logs ────────────────────────────────────────────────────────────
  const { data: logData, isLoading } = useQuery({
    queryKey: ["warehouse-merge-logs", pagination.pageIndex, pagination.pageSize, search],
    queryFn: async () => {
      const res = await httpClient.get(
        `warehouse_batch_merge_report/?page_number=${pagination.pageIndex + 1}&page_size=${pagination.pageSize}&search=${search}`
      );
      return res.data;
    },
    keepPreviousData: true,
  });

  // ─── Columns ───────────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        header: "Date",
        accessorKey: "created_at",
        size: "auto",
        Cell: ({ cell }) => moment(cell.getValue()).format("DD-MM-YYYY HH:mm"),
      },
      {
        header: "Product Code",
        accessorKey: "product_code",
        size: "auto",
      },
      {
        header: "Product Name",
        accessorKey: "product_title",
        size: "auto",
        Cell: ({ row }) => (
          <Box>
            <Text fw={700} size="sm">{row.original.product_title}</Text>
            <Text size="xs" color="dimmed">{row.original.product_code}</Text>
          </Box>
        ),
      },
      {
        header: "Target Batch",
        accessorKey: "target_batch_no",
        size: "auto",
        Cell: ({ cell }) => (
          <Badge
            variant="gradient"
            gradient={{ from: 'indigo', to: 'cyan' }}
            size="lg"
            radius="sm"
            sx={{ letterSpacing: 0.5, fontWeight: 700 }}
          >
            {cell.getValue()}
          </Badge>
        ),
      },
      {
        header: "Merge Variation (Stock Flow)",
        id: "variation",
        size: "auto",
        Cell: ({ row }) => {
          if (row.original.target_batch_current_stock === undefined) {
            return <Text size="xs" color="dimmed" italic>Syncing live data...</Text>;
          }
          const liveQty = Number(row.original.target_batch_current_stock || 0);
          const mergedQty = Number(row.original.merged_qty || 0);
          const beforeQty = liveQty - mergedQty;

          return (
            <Flex align="center" gap={0} sx={{
              background: 'rgba(0,0,0,0.03)',
              borderRadius: '8px',
              padding: '4px 12px',
              border: '1px solid rgba(0,0,0,0.05)',
              width: 'fit-content'
            }}>
              <Box sx={{ textAlign: "center" }}>
                <Text size="9px" fw={800} color="dimmed" tt="uppercase" ls={1}>Before</Text>
                <Text fw={700} size="sm">{beforeQty.toFixed(3)}</Text>
              </Box>

              <Box px={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Text size="9px" fw={800} color="green" tt="uppercase" ls={1}>Added</Text>
                <Text fw={800} size="sm" color="green">+{mergedQty.toFixed(3)}</Text>
              </Box>

              <Box sx={{
                textAlign: "center",
                background: 'linear-gradient(135deg, #228be6 0%, #15aabf 100%)',
                padding: '4px 10px',
                borderRadius: '6px',
                color: 'white',
                boxShadow: '0 2px 4px rgba(21, 170, 191, 0.2)'
              }}>
                <Text size="9px" fw={800} tt="uppercase" ls={1} opacity={0.9}>Current Live</Text>
                <Text fw={800} size="sm">
                  {liveQty.toFixed(3)}
                </Text>
              </Box>
            </Flex>
          );
        },
      },
      {
        header: "Created By",
        accessorKey: "created_by_name",
        size: "auto",
        Cell: ({ cell }) => (
          <Flex align="center" gap={8}>
            <Box sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: '#e9ecef',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              fontWeight: 700,
              color: '#495057'
            }}>
              {cell.getValue()?.charAt(0).toUpperCase()}
            </Box>
            <Text size="sm" fw={500}>{cell.getValue()}</Text>
          </Flex>
        )
      },
    ],
    []
  );

  const handleSearch = () => {
    setSearch(searchInput);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  return (
    <>
      <Container fluid>
        <Box p={25}>
          <Flex justify="space-between" align="end" wrap="wrap" gap={10}>
            <Box>
              <Text size="lg" fw={600} mb={10}>
                Warehouse Batch Merge Report
              </Text>
              <AppBreadcrumb
                crumbs={[
                  { text: "Reports" },
                  { text: "Warehouse Merge Report" },
                ]}
              />
            </Box>
            <Flex align="flex-end" gap={10}>
              <TextInput
                placeholder="Search product..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.currentTarget.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                sx={{ width: isMobileScreen ? "100%" : 300 }}
                rightSection={
                  <ActionIcon onClick={handleSearch}>
                    <IconSearch size={16} />
                  </ActionIcon>
                }
              />
            </Flex>
          </Flex>
        </Box>
      </Container>

      <Container fluid mb={30}>
        <Box px={isMobileScreen ? 5 : 25}>
          <Box className="datatable-wrapper">
            <MantineReactTable
              columns={columns}
              data={logData?.data || []}
              isLoading={isLoading}
              state={{ isLoading, pagination }}
              manualPagination
              rowCount={logData?.count || 0}
              onPaginationChange={setPagination}
              enableExpandAll={false}
              renderDetailPanel={({ row }) => (
                <Box sx={{ padding: "10px 20px", background: "#f8f9fa" }}>
                  <Text size="xs" fw={700} mb={8} color="dimmed" transform="uppercase">
                    Source Batches for this merge:
                  </Text>
                  <Table verticalSpacing="xs" highlightOnHover withBorder withColumnBorders sx={{ background: "white", borderRadius: 4 }}>
                    <thead>
                      <tr>
                        <th style={{ fontSize: 12 }}>Source Batch</th>
                        <th style={{ fontSize: 12 }}>Quantity Transferred</th>
                      </tr>
                    </thead>
                    <tbody>
                      {row.original.items.map((item) => (
                        <tr key={item.id}>
                          <td><Badge color="gray" variant="outline" size="sm">{item.source_batch_no}</Badge></td>
                          <td><Text size="sm" fw={600}>{Number(item.qty).toFixed(3)}</Text></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Box>
              )}
              mantineTableProps={{ className: "datatable", striped: true }}
              initialState={{ density: "xs" }}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableColumnActions={false}
              enableGlobalFilter={false}
              enableStickyHeader
            />
          </Box>
        </Box>
      </Container>
    </>
  );
}
